// Instruments the "corrupted vdom → per-poll error storm" episodes behind
// 9WICKET-CLIENT-2HT / 2JH / 2K3 / 2J0 / 2K1: one mid-patch exception leaves a
// half-committed vnode tree, then every polling update re-throws until the user
// reloads. Sampled Sentry events land mid-storm, minutes after the trigger has
// left the breadcrumb window, so this module preserves what happened at the
// storm's start (candidate trigger error, last navigation) for beforeSend to
// attach. Recording must happen in the Vue errorHandler layer: sampleRate
// drops 99.5% of events before beforeSend ever runs.

// `property 'bum'` = unmountComponent destructuring a null instance — the
// unmount-side symptom of the same corrupted tree (9WICKET-CLIENT-2NN).
const CORRUPTION_PATTERNS = [
  /emitsOptions/,
  /insertBefore/,
  /parentNode/,
  /nextSibling/,
  /property 'bum'/
]

// FullMarket polls every ~1.2s, so storm repeats arrive well within this gap;
// a corruption error this long after the previous one is a new episode.
const STORM_GAP_MS = 10_000

// How far back a plain Vue error is still a candidate trigger for a storm
// that starts now.
const PRECEDING_ERROR_WINDOW_MS = 30_000

interface RecordedVueError {
  message: string
  info: string
  at: number
}

interface RecordedNavigation {
  from: string
  to: string
  at: number
}

interface StormEpisode {
  startedAt: number
  lastSeenAt: number
  firstMessage: string
  count: number
  precedingError: RecordedVueError | undefined
  navigation: RecordedNavigation | undefined
}

interface StormReport {
  phase: "start" | "repeat"
  startedAt: number
  ageMs: number
  count: number
  firstMessage: string
  precedingError: (RecordedVueError & { msBeforeStorm: number }) | undefined
  navigation: (RecordedNavigation & { msBeforeStorm: number }) | undefined
}

let lastPlainVueError: RecordedVueError | undefined
let lastNavigation: RecordedNavigation | undefined
let currentStorm: StormEpisode | undefined

export const isCorruptionMessage = (message: string): boolean =>
  CORRUPTION_PATTERNS.some(pattern => pattern.test(message))

export const recordNavigation = (from: string, to: string): void => {
  lastNavigation = { from, to, at: Date.now() }
}

export const recordVueError = (message: string, info: string): void => {
  const now = Date.now()

  if (!isCorruptionMessage(message)) {
    lastPlainVueError = { message, info, at: now }
    return
  }

  if (currentStorm && now - currentStorm.lastSeenAt < STORM_GAP_MS) {
    currentStorm.lastSeenAt = now
    currentStorm.count += 1
    return
  }

  currentStorm = {
    startedAt: now,
    lastSeenAt: now,
    firstMessage: message,
    count: 1,
    precedingError:
      lastPlainVueError && now - lastPlainVueError.at < PRECEDING_ERROR_WINDOW_MS
        ? lastPlainVueError
        : undefined,
    navigation: lastNavigation
  }
}

export const getActiveStorm = (): StormReport | undefined => {
  if (!currentStorm) return undefined

  // An episode whose last corruption error is older than the gap is over; a
  // corruption event that arrives later (e.g. via window.onerror instead of
  // the Vue errorHandler) must not be attributed to the stale snapshot.
  if (Date.now() - currentStorm.lastSeenAt >= STORM_GAP_MS) return undefined

  const { startedAt, count, firstMessage, precedingError, navigation } = currentStorm

  return {
    phase: count === 1 ? "start" : "repeat",
    startedAt,
    ageMs: Date.now() - startedAt,
    count,
    firstMessage,
    precedingError: precedingError && {
      ...precedingError,
      msBeforeStorm: startedAt - precedingError.at
    },
    navigation: navigation && {
      ...navigation,
      msBeforeStorm: startedAt - navigation.at
    }
  }
}

const NAVIGATION_GAP_BUCKETS: Array<[number, string]> = [
  [100, "<100ms"],
  [500, "<500ms"],
  [2_000, "<2s"],
  [10_000, "<10s"]
]

// Low-cardinality labels so the tag stays aggregatable in Sentry.
export const bucketNavigationGap = (ms: number | undefined): string => {
  if (ms === undefined) return "none"
  const bucket = NAVIGATION_GAP_BUCKETS.find(([limit]) => ms < limit)
  return bucket ? bucket[1] : ">=10s"
}
